import { initial, intersection, last, set, sortBy } from 'lodash-es';
import { endOfWeek } from 'date-fns';
import {
  HubsDataDictionary,
  RegionsDataDictionary,
  ResourceViolationItem,
  ServiceRulesViolations,
  ServiceViolationItem,
  SupportViolationItem,
  ViolationDayType,
} from '@lp/features/Scheduler/types';
import { getObjectEntries, toLocalDateFromApiDateKey } from '@lp/features/Scheduler/utils';
import { OrganType } from '@/api/apis/case';
import {
  BaseResource,
  BaseShift,
  ByFlagRuleViolationName,
  CCShiftType,
  ComboRangeRuleViolation,
  CriticalDailySlotViolatedRuleName,
  CriticalDailyViolatedRuleName,
  CriticalResourceViolatedRuleName,
  CSShiftTimeGroup,
  RangeRuleViolation,
  ResourcesWithShifts,
  ShiftInRowRuleViolation,
  ShiftType,
  ShiftTypeNeighborRule,
  ServiceShiftTimeGroupRulesViolations,
  ServiceShiftTypeRulesViolations,
  ShiftTypeResourceRulesViolations,
  ShiftTypeScheduleValidationResult,
  ShiftTypeServiceRulesViolations,
  ShiftTypeServicesRulesViolations,
  ShiftTypeShiftAllowedDayOfWeekRuleViolations,
  ShiftTypeShiftNeighborRuleViolation,
  ShiftTypeShiftNeighborRuleViolations,
  CSShiftType,
} from '@/api/apis/resources/schedule';
import { formatDate, getFullName } from '@/utils/helpers';
import { DayOfWeek } from '@/types/date.types';
import { shiftTimeGroupNames, shiftTypeNames } from '@/constants/schedule.constants';

const ALL_DAYS_OF_WEEK_COUNT = 7;

export const catchParseError = <F extends (...args: unknown[]) => string>(fn: F) => {
  try {
    return fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return '[PARSING ERROR]';
  }
};

const getDatePeriodString = (dayKey: string, dayType?: ViolationDayType) => {
  const startDate = toLocalDateFromApiDateKey(dayKey);
  const formattedStartDate = formatDate(startDate, 'MM/dd/yyyy');

  switch (dayType) {
    case ViolationDayType.Daily:
      return ` for ${formattedStartDate}`;
    case ViolationDayType.Weekly:
      const endDate = endOfWeek(startDate);
      return ` for the Week (${formattedStartDate} - ${formatDate(endDate, 'MM/dd/yyyy')})`;
    default:
      return '';
  }
};

/** "A", "A or B", "A, B or C" */
const joinWithConjunction = (items: string[], conjunction: 'and' | 'or') => {
  const initialItems = initial(items);
  const lastItem = last(items) ?? '';

  return initialItems.length > 0 ? `${initialItems.join(', ')} ${conjunction} ${lastItem}` : lastItem;
};

/**
 * Weekdays only add information to weekly violations (a daily one is always its own weekday),
 * and nothing when BE reports all seven days (the rule allows any day).
 */
const getViolatedDaysOfWeekString = (daysOfWeek?: DayOfWeek[], dayType?: ViolationDayType) => {
  if (dayType !== ViolationDayType.Weekly || !daysOfWeek?.length || daysOfWeek.length >= ALL_DAYS_OF_WEEK_COUNT) {
    return '';
  }

  return ` on ${joinWithConjunction(daysOfWeek, 'and')}`;
};

export const getViolationComboRangeRuleData = (violation: ComboRangeRuleViolation) => {
  const { actualValue, violatedRule } = violation;
  const { hard, soft } = violatedRule;
  const isHard =
    actualValue < (hard.inclusiveFrom ?? 0) || (Number.isFinite(hard.inclusiveTo) && actualValue > hard.inclusiveTo!);
  const { inclusiveFrom, inclusiveTo } = isHard ? hard : soft;
  const isBelow = actualValue < (inclusiveFrom ?? 0);
  const remainCount = isBelow ? inclusiveFrom! - actualValue : actualValue - inclusiveTo!;

  return { isBelow, isHard, remainCount };
};

type ParseComboRangeRuleArgsType<T extends ShiftType> = {
  violation: ComboRangeRuleViolation;
  shiftType: T;
  dayKey: string;
  organ?: OrganType;
  dayType?: ViolationDayType;
  byFlagName?: ByFlagRuleViolationName;
  positionName?: string;
  timeGroup?: CSShiftTimeGroup;
  daysOfWeek?: DayOfWeek[];
};

export const parseComboRangeRule = <T extends ShiftType>({
  violation,
  shiftType,
  dayKey,
  organ,
  dayType,
  byFlagName,
  positionName = 'Specialists',
  timeGroup,
  daysOfWeek,
}: ParseComboRangeRuleArgsType<T>) => {
  const { isBelow, remainCount } = getViolationComboRangeRuleData(violation);
  const isOnCallDuty = byFlagName === ByFlagRuleViolationName.IsManager && shiftType === CCShiftType.OnCall;
  const timeGroupStr = timeGroup ? ` (Group ${shiftTimeGroupNames[timeGroup]})` : '';
  const shiftTypeName = shiftTypeNames[shiftType] + (isOnCallDuty ? ' (Duty)' : '') + timeGroupStr;
  const organStr = organ ? ` ${organ}` : '';
  const periodStr = getDatePeriodString(dayKey, dayType) + getViolatedDaysOfWeekString(daysOfWeek, dayType);

  return isBelow
    ? `Insufficient number of${organStr} ${positionName} ${shiftTypeName}${periodStr}; ${remainCount} more shifts are needed.`
    : `Exceeded the maximum number of${organStr} ${positionName} ${shiftTypeName}${periodStr}; ${remainCount} shifts over the limit.`;
};

type ServiceComboViolationsArgsType<T extends ShiftType> = {
  violation: ServiceShiftTimeGroupRulesViolations;
  shiftType: T;
  dayKey: string;
  dayType?: ViolationDayType;
  timeGroup?: CSShiftTimeGroup;
};

const getServiceGlobalViolationMessage = <T extends ShiftType>({
  violation,
  ...args
}: ServiceComboViolationsArgsType<T>) => {
  const { global, globalViolatedDaysOfWeek } = violation;
  if (!global) return undefined;

  return catchParseError(() =>
    parseComboRangeRule({ ...args, violation: global, daysOfWeek: globalViolatedDaysOfWeek }),
  );
};

const getServiceByOrganViolationMessages = <T extends ShiftType>({
  violation,
  ...args
}: ServiceComboViolationsArgsType<T>) => {
  if (!violation.byOrganType) return [];

  return getObjectEntries(violation.byOrganType).map(([organ, v]) => {
    const daysOfWeek = violation.byOrganTypeViolatedDaysOfWeek?.[organ];
    return [organ, catchParseError(() => parseComboRangeRule({ ...args, violation: v, organ, daysOfWeek }))] as const;
  });
};

const getServiceByFlagViolationMessages = <T extends ShiftType>({
  violation,
  ...args
}: ServiceComboViolationsArgsType<T>) => {
  if (!violation.byFlag) return [];

  return getObjectEntries(violation.byFlag).map(([byFlagName, v]) =>
    catchParseError(() => parseComboRangeRule({ ...args, violation: v, byFlagName })),
  );
};

/** CS period path: hub/region On Call coverage sliced by Shift Time Group (group → organ → violation). */
const getServiceTimeGroupsViolations = (
  violation: ServiceShiftTypeRulesViolations,
): [CSShiftTimeGroup, ServiceShiftTimeGroupRulesViolations][] => {
  if (!violation.byShiftTimeGroup) return [];

  return sortBy(getObjectEntries(violation.byShiftTimeGroup), ([timeGroup]) => timeGroup);
};

const getShiftTypeServiceViolationMessages = <T extends ShiftType>(
  violations: ShiftTypeServiceRulesViolations<T>,
  dayKey: string,
  dayType?: ViolationDayType,
) => {
  return getObjectEntries(violations).flatMap(([shiftType, violation]) => {
    const getMessages = (v: ServiceShiftTimeGroupRulesViolations, timeGroup?: CSShiftTimeGroup) => {
      const args = { violation: v, shiftType, dayKey, dayType, timeGroup };

      return [
        getServiceGlobalViolationMessage(args),
        ...getServiceByOrganViolationMessages(args).map(([, message]) => message),
        ...getServiceByFlagViolationMessages(args),
      ].filter((message): message is string => Boolean(message));
    };

    return [
      ...getMessages(violation),
      ...getServiceTimeGroupsViolations(violation).flatMap(([timeGroup, v]) => getMessages(v, timeGroup)),
    ];
  });
};

type ShiftTypeServiceViolationMessagesByOrganType = Partial<Record<'global' | OrganType, string[]>>;

export const getShiftTypeServiceViolationMessagesByOrgan = <T extends ShiftType>(
  violations: ShiftTypeServiceRulesViolations<T>,
  dayKey: string,
  dayType?: ViolationDayType,
) => {
  const messagesByOrganMap = {} as ShiftTypeServiceViolationMessagesByOrganType;

  const pushMessage = (key: 'global' | OrganType, message: string) => {
    if (!messagesByOrganMap[key]) messagesByOrganMap[key] = [];
    messagesByOrganMap[key].push(message);
  };

  getObjectEntries(violations).forEach(([shiftType, violation]) => {
    const collect = (v: ServiceShiftTimeGroupRulesViolations, timeGroup?: CSShiftTimeGroup) => {
      const args = { violation: v, shiftType, dayKey, dayType, timeGroup };
      const globalMessage = getServiceGlobalViolationMessage(args);

      if (globalMessage) pushMessage('global', globalMessage);
      getServiceByOrganViolationMessages(args).forEach(([organ, message]) => pushMessage(organ, message));
    };

    collect(violation);
    getServiceTimeGroupsViolations(violation).forEach(([timeGroup, v]) => collect(v, timeGroup));
  });

  return messagesByOrganMap;
};

export const transformHubAndRegionViolations = <T extends ShiftType>(
  violationsData?: ShiftTypeScheduleValidationResult<T>,
  hubsDictionary?: HubsDataDictionary,
  regionsDictionary?: RegionsDataDictionary,
) => {
  const byHubMap: Record<string, ServiceRulesViolations> = {};
  const byRegionMap: Record<string, ServiceRulesViolations> = {};
  const hubsMessagesList: ServiceViolationItem[] = [];
  const regionsMessagesList: ServiceViolationItem[] = [];

  if (violationsData && hubsDictionary && regionsDictionary) {
    const mutator = (
      dayType: ViolationDayType,
      dataPath: keyof Omit<ShiftTypeServicesRulesViolations<T>, 'countryRulesViolations'>,
    ) => {
      const { dailyRulesViolations, weeklyRulesViolations } = violationsData;
      const data = dayType === ViolationDayType.Daily ? dailyRulesViolations : weeklyRulesViolations;
      const objToMutate = dataPath === 'hubsRulesViolations' ? byHubMap : byRegionMap;
      const listToMutate = dataPath === 'hubsRulesViolations' ? hubsMessagesList : regionsMessagesList;
      const dictionary = dataPath === 'hubsRulesViolations' ? hubsDictionary : regionsDictionary;

      Object.entries(data).forEach(([dayKey, serviceViolations]) => {
        Object.entries(serviceViolations[dataPath]).forEach(([serviceId, shiftTypeViolations]) => {
          const messages = getShiftTypeServiceViolationMessages(shiftTypeViolations, dayKey, dayType);
          const title = dictionary[serviceId]?.name ?? '[UNKNOWN]';
          const item: ServiceViolationItem = { id: serviceId, day: dayKey, title, messages, dayType };

          set(objToMutate, [serviceId, dayType, dayKey], item);
          listToMutate.push(item);
        });
      });
    };

    mutator(ViolationDayType.Daily, 'hubsRulesViolations');
    mutator(ViolationDayType.Daily, 'regionsRulesViolations');
    mutator(ViolationDayType.Weekly, 'hubsRulesViolations');
    mutator(ViolationDayType.Weekly, 'regionsRulesViolations');
  }

  return {
    byHubMap,
    byRegionMap,
    hubsMessagesList: sortBy(hubsMessagesList, ['title', 'day']),
    regionsMessagesList: sortBy(regionsMessagesList, ['title', 'day']),
  };
};

export const transformCountryViolations = <T extends ShiftType>(
  violationsData?: ShiftTypeScheduleValidationResult<T>,
) => {
  const byDayMap: ServiceRulesViolations = {};
  const dayMessagesList: ServiceViolationItem[] = [];

  if (violationsData) {
    const violationDayKeys = ['dailyRulesViolations', 'weeklyRulesViolations'] as const;

    violationDayKeys.forEach((violationDayKey) => {
      const dayType = violationDayKey === 'dailyRulesViolations' ? ViolationDayType.Daily : ViolationDayType.Weekly;

      getObjectEntries(violationsData[violationDayKey]).forEach(([dayKey, serviceViolations]) => {
        const violation = serviceViolations.countryRulesViolations;
        const messages = getShiftTypeServiceViolationMessages(violation, dayKey, dayType);
        const title = formatDate(toLocalDateFromApiDateKey(dayKey), 'MM/dd/yyyy');
        const item: ServiceViolationItem = { day: dayKey, dayType, title, messages };

        set(byDayMap, [dayType, dayKey], item);
        dayMessagesList.push(item);
      });
    });
  }

  return { byDayMap, dayMessagesList };
};

// Support
export const transformCountrySupportViolations = <T extends ShiftType>(
  violationsData?: ShiftTypeScheduleValidationResult<T>,
) => {
  if (!violationsData) return [];

  return sortBy(
    getObjectEntries(violationsData.dailyRulesViolations)
      .map(([dayKey, serviceViolations]) => {
        const violation = serviceViolations.countryRulesViolations;
        const messages = getShiftTypeServiceViolationMessages(violation, dayKey, ViolationDayType.Daily);
        const title = formatDate(toLocalDateFromApiDateKey(dayKey), 'MM/dd/yyyy');

        return { day: dayKey, title, messages } as SupportViolationItem;
      })
      .filter((v) => v.messages.length > 0),
    'day',
  );
};

// Resources
const parseRangeRuleViolation = (ruleViolation: RangeRuleViolation, shiftNamesText: string) => {
  const { actualValue, violatedRule } = ruleViolation ?? {};
  const isBelow = actualValue < (violatedRule.inclusiveFrom ?? 0);
  const remainCount = isBelow ? violatedRule.inclusiveFrom! - actualValue : actualValue - violatedRule.inclusiveTo!;

  return isBelow
    ? `Insufficient ${shiftNamesText} shifts in total; ${remainCount} more shifts are needed.`
    : `Exceeded the maximum number of ${shiftNamesText} shifts in total; ${remainCount} shifts over the limit.`;
};

const parseResourceMonthlyShiftCountViolation = <T extends ShiftType>(
  ruleViolation: RangeRuleViolation,
  shiftType: T,
  isWeekEnd?: boolean,
) => {
  const { actualValue, violatedRule } = ruleViolation ?? {};
  const isBelow = actualValue < (violatedRule.inclusiveFrom ?? 0);
  const remainCount = isBelow ? violatedRule.inclusiveFrom! - actualValue : actualValue - violatedRule.inclusiveTo!;
  const shiftTypeName = shiftTypeNames[shiftType];
  const onWeekEndString = isWeekEnd ? ' on a Weekend' : '';

  return isBelow
    ? `Insufficient ${shiftTypeName} shifts${onWeekEndString}; ${remainCount} more shifts are needed.`
    : `Exceeded the maximum number of ${shiftTypeName} shifts${onWeekEndString}; ${remainCount} shifts over the limit.`;
};

const parseResourceShiftTypeInRowViolation = <T extends ShiftType>(
  rule: ShiftInRowRuleViolation['violatedRule'],
  violation: ShiftInRowRuleViolation['violations'][number],
  shiftType: T,
) => {
  const { hard, soft } = rule;
  const { actualLengthInRow: actualLength } = violation;
  const isHard = actualLength < (hard.inclusiveFrom ?? 0) || (hard.inclusiveTo && actualLength > hard.inclusiveTo);
  const range = isHard ? `${hard.inclusiveFrom} - ${hard.inclusiveTo}` : `${soft.inclusiveFrom} - ${soft.inclusiveTo}`;
  const shiftTypeName = shiftTypeNames[shiftType];

  return `Insufficient ${shiftTypeName} shifts in a row (${actualLength}); required ${range} in-row.`;
};

type NeighbourSide = 'before' | 'after';

/** Both sides can be broken at once, e.g. a Clinical Lead day between two On Call days. */
const getViolatedNeighbourSides = <T extends ShiftType>(
  rule: ShiftTypeNeighborRule<T>,
  violation: ShiftTypeShiftNeighborRuleViolation<T>,
): NeighbourSide[] => {
  const { daysBefore, daysAfter, beforeShiftTypes, afterShiftTypes } = rule;
  const { actualBefore, actualAfter } = violation;
  const isSideViolated = (days: number, expected: T[], actual: T[]) =>
    days > 0 && actual.length > 0 && intersection(expected, actual).length === 0;
  const sides: NeighbourSide[] = [];

  if (isSideViolated(daysBefore, beforeShiftTypes, actualBefore)) sides.push('before');
  if (isSideViolated(daysAfter, afterShiftTypes, actualAfter)) sides.push('after');

  return sides.length > 0 ? sides : ['before'];
};

const parseResourceShiftNeighbourViolation = <T extends ShiftType>(
  rule: ShiftTypeNeighborRule<T>,
  violation: ShiftTypeShiftNeighborRuleViolation<T>,
  shiftType: T,
  side: NeighbourSide,
) => {
  const isBefore = side === 'before';
  const date = formatDate(toLocalDateFromApiDateKey(violation.targetDate), 'MM/dd/yyyy');
  const expectedShifts = joinWithConjunction(
    (isBefore ? rule.beforeShiftTypes : rule.afterShiftTypes).map((s) => shiftTypeNames[s]),
    'or',
  );
  const actualShifts = (isBefore ? violation.actualBefore : violation.actualAfter)
    .map((s) => shiftTypeNames[s])
    .join(', ');
  const shiftTypeName = shiftTypeNames[shiftType];

  return `${expectedShifts} shifts expected instead of ${actualShifts} at ${date} ${side} ${shiftTypeName} shift`;
};

const parseResourceShiftAllowedDayOfWeekViolation = <T extends ShiftType>(
  allowedDays: DayOfWeek[],
  // violation: ShiftTypeShiftAllowedDayOfWeekRuleViolation, // May need in future for more detailed message
  shiftType: T,
) => {
  const shiftTypeName = shiftTypeNames[shiftType];

  return `${shiftTypeName} shift shall be assigned on ${joinWithConjunction(allowedDays, 'or')}.`;
};

const getShiftTypeResourceViolationMessages = <T extends ShiftType>(
  violations?: Record<T, RangeRuleViolation>,
  isWeekEnd?: boolean,
) => {
  if (!violations) return [];

  return getObjectEntries(violations).map(([shiftType, violation]) => {
    return catchParseError(() => parseResourceMonthlyShiftCountViolation(violation, shiftType, isWeekEnd));
  });
};

const getShiftOnCallPhoneSupportViolationMessages = (violation?: RangeRuleViolation | null) => {
  if (!violation) return [];

  return [
    catchParseError(() =>
      parseRangeRuleViolation(
        violation,
        `${shiftTypeNames[CSShiftType.OnCall]} and ${shiftTypeNames[CSShiftType.PhoneSupport]}`,
      ),
    ),
  ];
};

const getShiftCountNoCallWeekEndViolationMessages = (violation?: RangeRuleViolation | null) => {
  if (!violation) return [];

  return [
    catchParseError(() =>
      parseRangeRuleViolation(
        violation,
        `${shiftTypeNames[CSShiftType.NoCall]} and ${shiftTypeNames[CSShiftType.WeekendOff]}`,
      ),
    ),
  ];
};

const getShiftCountFlexDaysViolationMessages = (violation?: RangeRuleViolation | null) => {
  if (!violation) return [];

  return [
    catchParseError(() =>
      parseRangeRuleViolation(
        violation,
        `Flex Day (${shiftTypeNames[CSShiftType.AtHub]}, ${shiftTypeNames[CSShiftType.OfficeDay]} and ${shiftTypeNames[CSShiftType.TravelDay]})`,
      ),
    ),
  ];
};

const getShiftTypeInRowResourceViolationMessages = <T extends ShiftType>(
  violations?: Record<T, ShiftInRowRuleViolation>,
) => {
  if (!violations) return [];

  return getObjectEntries(violations).flatMap(([shiftType, ruleViolation]) => {
    return ruleViolation.violations.map((violation) => {
      return catchParseError(() =>
        parseResourceShiftTypeInRowViolation(ruleViolation.violatedRule, violation, shiftType),
      );
    });
  });
};

const getShiftNeighbourResourceViolationMessages = <T extends ShiftType>(
  violations?: Record<T, ShiftTypeShiftNeighborRuleViolations<T>>,
) => {
  if (!violations) return [];

  return getObjectEntries(violations).flatMap(([shiftType, ruleViolation]) => {
    return ruleViolation.violations.flatMap((violation) => {
      return getViolatedNeighbourSides(ruleViolation.violatedRule, violation).map((side) =>
        catchParseError(() =>
          parseResourceShiftNeighbourViolation(ruleViolation.violatedRule, violation, shiftType, side),
        ),
      );
    });
  });
};

const getShiftAllowedDayOfWeekViolationMessages = <T extends ShiftType>(
  violations?: Record<T, ShiftTypeShiftAllowedDayOfWeekRuleViolations>,
) => {
  if (!violations) return [];

  return getObjectEntries(violations).flatMap(([shiftType, ruleViolation]) => {
    return ruleViolation.violations.map(() => {
      return catchParseError(() => parseResourceShiftAllowedDayOfWeekViolation(ruleViolation.actualRule, shiftType));
    });
  });
};

const getResourceViolationMessages = <T extends ShiftType>(resourceViolations: ShiftTypeResourceRulesViolations<T>) => {
  const {
    periodShiftCountByTypeViolations,
    periodShiftCountOnWeekEndViolations,
    periodShiftCountOnCallPhoneSupportViolation,
    periodShiftCountNoCallWeekEndViolation,
    periodShiftCountFlexDaysViolation,
    shiftTypeInRowViolations,
    shiftNeighborViolations,
    shiftAllowedDayOfWeekViolations,
  } = resourceViolations;

  return [
    getShiftTypeResourceViolationMessages(periodShiftCountByTypeViolations),
    getShiftTypeResourceViolationMessages(periodShiftCountOnWeekEndViolations, true),
    getShiftOnCallPhoneSupportViolationMessages(periodShiftCountOnCallPhoneSupportViolation),
    getShiftCountNoCallWeekEndViolationMessages(periodShiftCountNoCallWeekEndViolation),
    getShiftCountFlexDaysViolationMessages(periodShiftCountFlexDaysViolation),
    getShiftTypeInRowResourceViolationMessages(shiftTypeInRowViolations),
    getShiftNeighbourResourceViolationMessages(shiftNeighborViolations),
    getShiftAllowedDayOfWeekViolationMessages(shiftAllowedDayOfWeekViolations),
  ].flat();
};

export const transformResourcesViolations = <R extends BaseResource, T extends ShiftType, S extends BaseShift<T>>(
  resourcesViolations?: Record<string, ShiftTypeResourceRulesViolations<T>>,
  shiftsData?: ResourcesWithShifts<R, S>,
) => {
  const byResourceMap: Record<string, ResourceViolationItem> = {};
  const resourcesMessagesList: ResourceViolationItem[] = [];

  if (resourcesViolations && shiftsData) {
    Object.entries(resourcesViolations).forEach(([resourceId, resourceViolations]) => {
      const resource = shiftsData[resourceId]?.resource;
      const fullName = getFullName(resource?.firstName, resource?.lastName);
      const messages = getResourceViolationMessages(resourceViolations);
      const item = { id: resourceId, title: fullName, messages };

      byResourceMap[resourceId] = item;
      resourcesMessagesList.push(item);
    });
  }

  return { byResourceMap, resourcesMessagesList: sortBy(resourcesMessagesList, 'title') };
};

export const getDailyAndWeeklyServiceViolations = (violations?: ServiceRulesViolations) => {
  const { daily = {}, weekly = {} } = violations ?? {};
  const dailyList = Object.values(daily);
  const weeklyList = Object.values(weekly);

  return sortBy([...dailyList, ...weeklyList], 'day');
};

export const getResourcePublishBlockerMessage = (ruleName: CriticalResourceViolatedRuleName) => {
  switch (ruleName) {
    case CriticalResourceViolatedRuleName.ResourceHasShiftEveryDay:
      return 'No shifts for every day';
    default:
      return '';
  }
};

export const getDailyPublishBlockerMessage = (ruleName: CriticalDailyViolatedRuleName, day: string) => {
  switch (ruleName) {
    case CriticalDailyViolatedRuleName.OneOnDutyPerDay:
      return `No ${shiftTypeNames[CSShiftType.OnCall]} (Duty) CC shift for ${formatDate(day, 'MM/dd/yyyy')}`;
    default:
      return '';
  }
};

export const getDailySlotPublishBlockerMessage = (
  ruleName: CriticalDailySlotViolatedRuleName,
  daySlotData: { Day: string; Slot: number },
) => {
  switch (ruleName) {
    case CriticalDailySlotViolatedRuleName.NoOnCallForOrgan:
      return `No ${shiftTypeNames[CSShiftType.OnCall]} shift (Slot ${daySlotData.Slot}) for ${formatDate(daySlotData.Day, 'MM/dd/yyyy')}`;
    default:
      return '';
  }
};
